import api from '../../utils/api';
import type {
  ComposeFormState,
  EmailSignature,
  MarketingEmailAnalytics,
  MarketingEmailCampaign,
  MarketingEmailContact,
  MarketingEmailSettings,
  MarketingEmailStats,
  MarketingEmailTemplate,
} from '../../models/MasterAdmin/marketingEmail';

const BASE = '/master-admin/marketing-emails';

export async function fetchBrevoStatus(): Promise<{
  success: boolean;
  data: { connected: boolean; email?: string; companyName?: string; plan?: string };
  message?: string;
}> {
  const response = await api.get(`${BASE}/brevo/status`);
  return response.data;
}

export async function fetchCampaigns(params?: {
  folder?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  data: {
    campaigns: MarketingEmailCampaign[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> {
  const response = await api.get(`${BASE}/campaigns`, { params });
  return response.data;
}

export async function getCampaign(id: string): Promise<{ success: boolean; data: MarketingEmailCampaign }> {
  const response = await api.get(`${BASE}/campaigns/${encodeURIComponent(id)}`);
  return response.data;
}

export async function fetchReplyDraft(
  id: string,
  mode: 'reply' | 'replyAll' = 'replyAll',
  from?: string
): Promise<{ success: boolean; data: ComposeFormState & { parentCampaignId?: string; inReplyTo?: string; references?: string } }> {
  const response = await api.get(`${BASE}/campaigns/${encodeURIComponent(id)}/reply-draft`, {
    params: { mode, ...(from ? { from } : {}) },
  });
  return response.data;
}

export async function createCampaign(
  body: ComposeFormState & { action?: 'draft' | 'send' | 'schedule'; scheduledAt?: string }
): Promise<{ success: boolean; data: MarketingEmailCampaign; message?: string }> {
  const response = await api.post(`${BASE}/campaigns`, mapComposeToApi(body));
  return response.data;
}

export async function updateCampaign(
  id: string,
  body: Partial<ComposeFormState> & { action?: 'draft' | 'send' | 'schedule' }
): Promise<{ success: boolean; data: MarketingEmailCampaign }> {
  const response = await api.put(`${BASE}/campaigns/${encodeURIComponent(id)}`, mapComposeToApi(body));
  return response.data;
}

export async function sendCampaign(
  id: string,
  scheduledAt?: string
): Promise<{ success: boolean; data: MarketingEmailCampaign; message?: string }> {
  const response = await api.post(`${BASE}/campaigns/${encodeURIComponent(id)}/send`, {
    ...(scheduledAt ? { scheduledAt } : {}),
  });
  return response.data;
}

export async function retryCampaign(
  id: string,
  body: { mode: 'send_now' | 'schedule'; scheduledAt?: string }
): Promise<{ success: boolean; data: MarketingEmailCampaign; message?: string }> {
  const response = await api.post(`${BASE}/campaigns/${encodeURIComponent(id)}/retry`, body);
  return response.data;
}

export async function deleteCampaign(id: string): Promise<{ success: boolean; message?: string }> {
  const response = await api.delete(`${BASE}/campaigns/${encodeURIComponent(id)}`);
  return response.data;
}

export async function bulkDeleteCampaigns(
  ids: string[]
): Promise<{
  success: boolean;
  message?: string;
  data?: { moved: number; deleted: number; processed: number; requested: number };
}> {
  const response = await api.post(`${BASE}/campaigns/bulk-delete`, { ids });
  return response.data;
}

export async function emptyTrash(): Promise<{
  success: boolean;
  message?: string;
  data?: { deleted: number };
}> {
  const response = await api.delete(`${BASE}/campaigns/trash/all`);
  return response.data;
}

export async function toggleCampaignStar(
  id: string
): Promise<{ success: boolean; data: MarketingEmailCampaign }> {
  const response = await api.post(`${BASE}/campaigns/${encodeURIComponent(id)}/star`);
  return response.data;
}

export async function estimateRecipients(body: {
  audienceSegment: string;
  recipientMode: string;
  to?: string[];
}): Promise<{ success: boolean; data: { estimatedRecipients: number } }> {
  const response = await api.post(`${BASE}/campaigns/estimate-recipients`, body);
  return response.data;
}

export async function fetchTemplates(): Promise<{
  success: boolean;
  data: { templates: MarketingEmailTemplate[] };
}> {
  const response = await api.get(`${BASE}/templates`);
  return response.data;
}

export async function createTemplate(
  body: Pick<MarketingEmailTemplate, 'name' | 'description' | 'subject' | 'htmlBody'>
): Promise<{ success: boolean; data: MarketingEmailTemplate }> {
  const response = await api.post(`${BASE}/templates`, body);
  return response.data;
}

export async function updateTemplate(
  id: string,
  body: Partial<Pick<MarketingEmailTemplate, 'name' | 'description' | 'subject' | 'htmlBody'>>
): Promise<{ success: boolean; data: MarketingEmailTemplate }> {
  const response = await api.put(`${BASE}/templates/${encodeURIComponent(id)}`, body);
  return response.data;
}

export async function deleteTemplate(id: string): Promise<{ success: boolean }> {
  const response = await api.delete(`${BASE}/templates/${encodeURIComponent(id)}`);
  return response.data;
}

export async function fetchSignatures(): Promise<{
  success: boolean;
  data: { signatures: EmailSignature[] };
}> {
  const response = await api.get(`${BASE}/signatures`);
  return response.data;
}

export async function saveSignatures(
  signatures: EmailSignature[]
): Promise<{ success: boolean; data: { signatures: EmailSignature[] } }> {
  const response = await api.put(`${BASE}/signatures`, { signatures });
  return response.data;
}

export async function deleteSignature(id: string): Promise<{ success: boolean }> {
  const response = await api.delete(`${BASE}/signatures/${encodeURIComponent(id)}`);
  return response.data;
}

export async function fetchSettings(): Promise<{ success: boolean; data: MarketingEmailSettings }> {
  const response = await api.get(`${BASE}/settings`);
  return response.data;
}

export async function updateSettings(
  body: Partial<Pick<MarketingEmailSettings, 'defaultFromEmail' | 'fromEmails' | 'signatureImages'>>
): Promise<{ success: boolean; data: MarketingEmailSettings }> {
  const response = await api.put(`${BASE}/settings`, body);
  return response.data;
}

export async function uploadSignatureImages(
  files: File[]
): Promise<{ success: boolean; data: MarketingEmailSettings; message?: string }> {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file, file.name));
  const response = await api.post(`${BASE}/settings/signature-images`, formData);
  return response.data;
}

export async function removeSignatureImage(
  imageId: string
): Promise<{ success: boolean; data: MarketingEmailSettings }> {
  const response = await api.delete(`${BASE}/settings/signature-images/${encodeURIComponent(imageId)}`);
  return response.data;
}

export async function fetchContacts(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; data: { contacts: MarketingEmailContact[]; total: number } }> {
  const response = await api.get(`${BASE}/contacts`, { params });
  return response.data;
}

export async function deleteContact(id: string): Promise<{ success: boolean }> {
  const response = await api.delete(`${BASE}/contacts/${encodeURIComponent(id)}`);
  return response.data;
}

export async function fetchAnalytics(params?: {
  days?: number;
}): Promise<{ success: boolean; data: MarketingEmailAnalytics }> {
  const response = await api.get(`${BASE}/analytics`, { params });
  return response.data;
}

export async function fetchBrevoAggregatedAnalytics(params?: {
  days?: number;
  tag?: string;
}): Promise<{ success: boolean; data: { raw: Record<string, unknown>; stats: MarketingEmailStats; range: string | null } }> {
  const response = await api.get(`${BASE}/analytics/brevo/aggregated`, { params });
  return response.data;
}

export async function fetchBrevoEventAnalytics(params?: {
  days?: number;
  limit?: number;
  offset?: number;
  event?: string;
  tags?: string;
  email?: string;
}): Promise<{ success: boolean; data: { events: Array<Record<string, unknown>> } }> {
  const response = await api.get(`${BASE}/analytics/brevo/events`, { params });
  return response.data;
}

export async function fetchBrevoDailyReports(params?: {
  days?: number;
  limit?: number;
  offset?: number;
  tag?: string;
}): Promise<{ success: boolean; data: { reports: Array<Record<string, unknown>> } }> {
  const response = await api.get(`${BASE}/analytics/brevo/reports`, { params });
  return response.data;
}

export async function syncAnalyticsFromBrevo(body?: {
  days?: number;
  limit?: number;
  maxPages?: number;
}): Promise<{
  success: boolean;
  message?: string;
  data?: {
    eventsFetched: number;
    campaignsUpdated: number;
    analytics: MarketingEmailAnalytics;
  };
}> {
  const response = await api.post(`${BASE}/analytics/sync-brevo`, body || {});
  return response.data;
}

function mapComposeToApi(body: Partial<ComposeFormState> & { action?: string; scheduledAt?: string }) {
  return {
    from: body.from,
    to: body.to,
    cc: body.cc,
    bcc: body.bcc,
    replyTo: body.replyTo,
    parentCampaignId: body.parentCampaignId,
    inReplyTo: body.inReplyTo,
    references: body.references,
    subject: body.subject,
    htmlBody: body.htmlBody,
    audienceSegment: body.audienceSegment,
    recipientMode: body.recipientMode,
    sendIndividually: body.sendIndividually,
    includeSignature: body.includeSignature,
    signatureId: body.signatureId,
    attachments: body.attachments,
    scheduledAt: body.scheduledAt,
    action: body.action,
  };
}
